import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/library/select";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  CalendarDays,
  FileText,
} from "lucide-react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isSameDay,
  isBefore,
  isAfter,
  startOfDay,
  addMonths,
  subMonths,
  setMonth,
} from "date-fns";

const DEFAULT_SCHEDULE_LABELS = {
  noteTitle: "Add follow-up note",
  notePlaceholder: "Type your follow-up note...",
  noteHint:
    "Note is optional. You can skip and create the follow-up without one.",
  submit: "Create Follow-up",
} as const;

export type DatePickerScheduleLabels = {
  noteTitle: string;
  notePlaceholder: string;
  noteHint: string;
  submit: string;
};

interface DatePickerCalendarProps {
  value?: Date | string;
  onChange?: (date: Date | null) => void;
  onCreateFollowUp?: (data: { date: Date; note: string }) => void;
  /**
   * "Whose follow-up are you setting" control, rendered INSIDE the popover
   * on the Date step (user directive 2026-08-30: ask in the modal, not
   * beside it). Only manager/admin-scoped viewers pass this — personal-scope
   * users are never asked, their own slot is implicit.
   */
  slotPicker?: React.ReactNode;
  /**
   * True while the slot picker still needs a choice — there is NO "Auto" for
   * managers ("if it is a manager you gotta select a role"), so the Date
   * step's Next stays disabled until a role is picked.
   */
  slotPickerPending?: boolean;
  /** Tooltip on the disabled Next while `slotPickerPending` (default: pick a role). */
  slotPickerPendingReason?: string;
  /**
   * Last selectable day (inclusive). Days after it are greyed out like past
   * days. Follow-up call sites derive it from the entity's stage / status /
   * sub-status "Maximum days allowed" dial (`followUpMaxDate`); the server
   * rejects a later date regardless.
   */
  maxDate?: Date | null;
  /** Full sentence shown under the grid (and as the greyed days' tooltip)
   *  while `maxDate` is set; defaults to "…can be set until <date>." */
  maxDateHint?: string;
  /** When set (e.g. deadlines), overrides copy on the note + submit step. */
  scheduleLabels?: DatePickerScheduleLabels;
  /**
   * Show the "Clear" action. Defaults to true for DEADLINES, which are
   * genuinely optional and may be removed.
   *
   * Follow-up call sites pass `false`: a follow-up is a single always-live
   * date that can only be RESCHEDULED (2026-08-20). Clearing one would put
   * the lead back into the "no follow-up" state the server now counts as due
   * today anyway, so the button promised something it could not deliver.
   * This component is SHARED with `BulkDeadline`, hence a prop rather than
   * deleting the button.
   */
  allowClear?: boolean;
  /**
   * Whether this picker asks for a TIME.
   *
   * Follow-up call sites pass the tenant's `useFollowUpTimeEnabled()` flag: a
   * tenant that works follow-ups by day only gets Date → Note, and the date is
   * emitted at the START of the chosen day (00:00 local = 00:00 IST for these
   * tenants), which is the date-only shape the server stores and the reminder
   * sweep already understands.
   *
   * Deadlines keep the default `true` — the setting is about follow-ups, and
   * this component is shared with `BulkDeadline`.
   */
  enableTime?: boolean;
  /**
   * Where the panel opens relative to its trigger. The default (above/below,
   * start-aligned) suits the drawer's BOTTOM BAR, where this picker was born.
   * A trigger in the MIDDLE of a panel (the Tracks tab's follow-up chip) has
   * no room either way and the panel ends up over the drawer header — such a
   * caller opens it to the side instead.
   */
  popoverSide?: "top" | "bottom" | "left" | "right";
  popoverAlign?: "start" | "center" | "end";
  children: React.ReactNode;
}

type Step = "date" | "time" | "note";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES_5 = Array.from({ length: 12 }, (_, i) => i * 5);

function getDefaultTime(selectedDate: Date): {
  hour: number;
  minute: number;
  period: "AM" | "PM";
} {
  const today = startOfDay(new Date());

  if (isSameDay(selectedDate, today)) {
    // Local clock, NOT an IST projection: isTimePast and buildFinalDate both
    // work in browser-local time, and the mobile picker does the same. Seeding
    // the default from a different clock than the one that judges it meant a
    // browser east of IST opened today's picker already flagged "time has
    // passed" with Next disabled — same-day follow-ups looked impossible.
    const now = new Date();
    let h = now.getHours() + 1;
    let m = Math.ceil(now.getMinutes() / 5) * 5;
    if (m >= 60) {
      m = 0;
      h++;
    }
    // Rolling past 23:00 used to wrap to 0 — offering "12:00 AM" of the day
    // ALREADY chosen, i.e. a time in the past, which the past-time guard then
    // refuses (Next stayed disabled for the whole of today). Clamp to the last
    // slot of the day instead, matching the mobile picker.
    if (h >= 24) {
      h = 23;
      m = 55;
    }
    const period: "AM" | "PM" = h >= 12 ? "PM" : "AM";
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return { hour: hour12, minute: m, period };
  }

  return { hour: 10, minute: 0, period: "AM" };
}

function ScrollSelector({
  items,
  selected,
  onSelect,
  formatFn,
}: {
  items: number[];
  selected: number;
  onSelect: (v: number) => void;
  formatFn?: (v: number) => string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const scrollEndTimer = useRef<number | null>(null);
  const hasCentered = useRef(false);
  // Set when the value below was committed by the user scrolling the column,
  // so the recentre effect can skip that case. Recentring a scroll the user
  // just performed re-enters onScroll, which commits again — the column ends
  // up fighting the gesture and reads as "the wheel won't scroll".
  const committedByScroll = useRef(false);

  const centerOn = (value: number, behavior: ScrollBehavior) => {
    const container = containerRef.current;
    const el = itemRefs.current[value];
    if (!container || !el) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    container.scrollTo({
      top:
        container.scrollTop +
        (elRect.top - containerRect.top) -
        containerRect.height / 2 +
        elRect.height / 2,
      behavior,
    });
  };

  // Programmatic snap when `selected` changes (popover open, time defaulting,
  // or click-select). The scroll-snap CSS below handles the user-drag case;
  // this just keeps the centered item in sync when the value is set
  // externally.
  useEffect(() => {
    if (committedByScroll.current) {
      committedByScroll.current = false;
      return;
    }
    centerOn(selected, hasCentered.current ? "smooth" : "auto");
    hasCentered.current = true;
  }, [selected]);

  // Pick whichever item is closest to the column's vertical centre. Used
  // after the user finishes scrolling so dragging the wheel actually updates
  // the selected value (previously you could only click — scrolling moved the
  // column visually but never committed a new selection).
  const findCenterItem = (): number | null => {
    const container = containerRef.current;
    if (!container) return null;
    const containerRect = container.getBoundingClientRect();
    const centerY = containerRect.top + containerRect.height / 2;
    let bestValue: number | null = null;
    let bestDist = Infinity;
    for (const v of items) {
      const el = itemRefs.current[v];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const elCenter = r.top + r.height / 2;
      const dist = Math.abs(elCenter - centerY);
      if (dist < bestDist) {
        bestDist = dist;
        bestValue = v;
      }
    }
    return bestValue;
  };

  const handleScroll = () => {
    if (scrollEndTimer.current != null) {
      window.clearTimeout(scrollEndTimer.current);
    }
    // Debounce to "scroll-end" — fires once the user (or scroll-snap) settles.
    // Without this we'd thrash onSelect on every wheel tick.
    scrollEndTimer.current = window.setTimeout(() => {
      const center = findCenterItem();
      if (center != null && center !== selected) {
        committedByScroll.current = true;
        onSelect(center);
      }
    }, 140);
  };

  useEffect(() => {
    return () => {
      if (scrollEndTimer.current != null) {
        window.clearTimeout(scrollEndTimer.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="h-[160px] overflow-y-auto select-none"
      style={{
        scrollbarWidth: "none",
        // `proximity` lets short drags actually move the wheel; `mandatory`
        // was snapping every tiny scroll back to the current item, making
        // the picker feel locked.
        scrollSnapType: "y proximity",
        WebkitOverflowScrolling: "touch",
        // Allow vertical pan; prevent the scroll from bubbling up to the
        // popover / page so the wheel column owns the gesture.
        touchAction: "pan-y",
        overscrollBehavior: "contain",
      }}
    >
      {/* Top + bottom spacers center the first / last item in the viewport.
          Container is 160px; item is 40px; spacer = (160 - 40) / 2 = 60px. */}
      <div className="space-y-0.5">
        <div style={{ height: 60 }} aria-hidden />
        {items.map((v) => (
          <div
            key={v}
            ref={(el) => {
              itemRefs.current[v] = el;
            }}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(v)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(v);
              }
            }}
            style={{ scrollSnapAlign: "center", height: 40 }}
            className={`w-full flex items-center justify-center rounded-xl transition-all duration-150 cursor-pointer ${
              selected === v
                ? "bg-[#218760] text-white text-[18px] font-medium"
                : "text-[#697571] text-[14px] font-normal hover:bg-[#f7f9f8]"
            }`}
          >
            {formatFn ? formatFn(v) : v}
          </div>
        ))}
        <div style={{ height: 60 }} aria-hidden />
      </div>
    </div>
  );
}

export default function DatePickerCalendar({
  value,
  onChange,
  onCreateFollowUp,
  scheduleLabels,
  allowClear = true,
  enableTime = true,
  popoverSide,
  popoverAlign,
  slotPicker,
  slotPickerPending = false,
  slotPickerPendingReason,
  maxDate = null,
  maxDateHint,
  children,
}: DatePickerCalendarProps) {
  const copy = scheduleLabels ?? DEFAULT_SCHEDULE_LABELS;
  // An OVERDUE follow-up must not seed the picker: it opens the calendar on a
  // past month where every day is disabled (the user has to page forward to
  // find something clickable), and because the past date stays `selectedDate`,
  // Next → Next → Create silently wrote a follow-up in the past — one the
  // reminder sweep then marks reminded without ever ringing. Rescheduling an
  // overdue follow-up is exactly the "lead said call me this afternoon" case,
  // so it reseeds to TODAY, same as the mobile picker.
  const reseedPastToToday = (raw: Date): Date =>
    isBefore(startOfDay(raw), startOfDay(new Date())) ? new Date() : raw;
  const initialDate = value
    ? reseedPastToToday(typeof value === "string" ? new Date(value) : value)
    : new Date();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("date");
  const [currentMonth, setCurrentMonth] = useState(initialDate);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    initialDate || null
  );

  const [hour, setHour] = useState(10);
  const [minute, setMinute] = useState(0);
  const [period, setPeriod] = useState<"AM" | "PM">("AM");
  const [note, setNote] = useState("");

  const today = startOfDay(new Date());

  useEffect(() => {
    if (value) {
      const dateValue = reseedPastToToday(
        typeof value === "string" ? new Date(value) : value
      );
      setSelectedDate(dateValue);
      setCurrentMonth(dateValue);
    }
    // reseedPastToToday is a stable pure helper; only `value` matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (open) {
      setStep("date");
      setNote("");
    }
  }, [open]);

  // The org setting can land mid-flow (the config query refetches). Without
  // this, a picker sitting on the time step would keep a step the indicator no
  // longer lists; move it forward to the note instead.
  useEffect(() => {
    if (!enableTime && step === "time") setStep("note");
  }, [enableTime, step]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd,
  });

  const isPastDate = (date: Date) => isBefore(startOfDay(date), today);
  // Beyond the "Maximum days allowed" horizon — disabled exactly like the
  // past, so the calendar shows one contiguous window of pickable days.
  const isBeyondMax = (date: Date) =>
    maxDate != null && isAfter(startOfDay(date), startOfDay(maxDate));
  const isDisabledDay = (date: Date) => isPastDate(date) || isBeyondMax(date);

  const handleDateClick = (date: Date) => {
    if (isDisabledDay(date)) return;
    if (!isSameMonth(date, currentMonth)) setCurrentMonth(startOfMonth(date));
    setSelectedDate(date);
  };

  const handleDateOK = () => {
    if (!selectedDate) return;
    // Date-only tenant: there is no time to pick and no past-time to guard
    // against (the calendar already disables past DAYS), so go straight to the
    // note. The hour/minute state stays at its defaults and is never read —
    // `buildFinalDate` returns the start of the day instead.
    if (!enableTime) {
      setStep("note");
      return;
    }
    const defaults = getDefaultTime(selectedDate);
    setHour(defaults.hour);
    setMinute(defaults.minute);
    setPeriod(defaults.period);
    setStep("time");
  };

  const isToday = selectedDate ? isSameDay(selectedDate, today) : false;

  const isTimePast = useCallback((): boolean => {
    // No time captured → nothing can have "already passed" within today.
    if (!enableTime) return false;
    if (!isToday) return false;
    const now = new Date();
    let h24 = hour;
    if (period === "AM" && hour === 12) h24 = 0;
    else if (period === "PM" && hour !== 12) h24 = hour + 12;
    const selectedMinutes = h24 * 60 + minute;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return selectedMinutes <= nowMinutes;
  }, [enableTime, isToday, hour, minute, period]);

  const timePast = isTimePast();

  const handleTimeOK = () => {
    if (timePast) return;
    setStep("note");
  };

  const buildFinalDate = useCallback((): Date => {
    if (!selectedDate) return new Date();
    const d = new Date(selectedDate);
    // Date-only: emit 00:00 of the chosen day. The server flattens to 00:00 IST
    // anyway (`leadFormConfigService.resolveFollowUpDate`), so this is what the
    // caller's own optimistic copy should show too — and it is the exact shape
    // the reminder sweep reads as "date-only".
    if (!enableTime) {
      d.setHours(0, 0, 0, 0);
      return d;
    }
    let h24 = hour;
    if (period === "AM" && hour === 12) h24 = 0;
    else if (period === "PM" && hour !== 12) h24 = hour + 12;
    d.setHours(h24, minute, 0, 0);
    return d;
  }, [enableTime, selectedDate, hour, minute, period]);

  const handleCreate = () => {
    const finalDate = buildFinalDate();
    // Date-only: the only thing that can go stale while the note is typed is the
    // DAY (the popover sitting open across midnight). There is no time step to
    // bounce back to, so re-point at today and let the user press Create again.
    if (!enableTime) {
      const now = new Date();
      if (!selectedDate || isBefore(startOfDay(selectedDate), startOfDay(now))) {
        setSelectedDate(now);
        setCurrentMonth(now);
        setStep("date");
        return;
      }
      if (onChange) onChange(finalDate);
      if (onCreateFollowUp) onCreateFollowUp({ date: finalDate, note });
      setOpen(false);
      return;
    }
    // Last line of defence: the time-step guard runs when Next is clicked, but
    // the clock keeps moving while the note is typed (or the popover sits open
    // across midnight). A follow-up written in the past would never ring —
    // bounce back to the time step and let the user pick a slot still ahead.
    if (finalDate.getTime() <= Date.now()) {
      const now = new Date();
      if (!selectedDate || isBefore(startOfDay(selectedDate), startOfDay(now))) {
        // Crossed midnight: the chosen DAY is over, so the time step would
        // show no warning and Next would stay enabled — a silent
        // Create→bounce loop. Reseed to today with a fresh suggested slot.
        setSelectedDate(now);
        setCurrentMonth(now);
        const defaults = getDefaultTime(now);
        setHour(defaults.hour);
        setMinute(defaults.minute);
        setPeriod(defaults.period);
      }
      setStep("time");
      return;
    }
    if (onChange) onChange(finalDate);
    if (onCreateFollowUp) onCreateFollowUp({ date: finalDate, note });
    setOpen(false);
  };

  const handleClear = () => {
    setSelectedDate(null);
    if (onChange) onChange(null);
    setOpen(false);
  };

  const handleCancel = () => {
    if (step === "note") {
      // With no time step, "Back" from the note returns to the calendar.
      setStep(enableTime ? "time" : "date");
      return;
    }
    if (step === "time") {
      setStep("date");
      return;
    }
    setOpen(false);
  };

  // The Time chip is dropped entirely when the tenant is date-only — showing a
  // greyed-out step the flow never visits reads as a broken picker.
  const steps = enableTime
    ? ([
        { key: "date" as Step, icon: CalendarDays, label: "Date" },
        { key: "time" as Step, icon: Clock, label: "Time" },
        { key: "note" as Step, icon: FileText, label: "Note" },
      ] as const)
    : ([
        { key: "date" as Step, icon: CalendarDays, label: "Date" },
        { key: "note" as Step, icon: FileText, label: "Note" },
      ] as const);

  const stepIndicator = (
    <div className="flex items-center justify-center gap-2 py-2 border-b border-[#e6e8e7]">
      {steps.map((s, i) => {
        const Icon = s.icon;
        const isActive = step === s.key;
        const stepOrder = steps.map((x) => x.key);
        const isDone = stepOrder.indexOf(s.key) < stepOrder.indexOf(step);
        return (
          <div key={s.key} className="flex items-center gap-1.5">
            {i > 0 && (
              <div
                className={`w-6 h-px ${isDone ? "bg-[#218760]" : "bg-[#e6e8e7]"}`}
              />
            )}
            <div
              className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-all ${
                isActive
                  ? "bg-[#eaf5f1] text-[#218760]"
                  : isDone
                    ? "text-[#218760]"
                    : "text-[#9ba3a0]"
              }`}
            >
              <Icon className="size-3" />
              <span className="text-[10px] font-medium">{s.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        // Never taller than the space Radix found for it: on a short window the
        // panel scrolls inside itself instead of sliding under the app header.
        className="w-auto p-0 rounded-[24px] border-[#e6e8e7] shadow-[0px_4px_32px_0px_rgba(0,0,0,0.08)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto"
        align={popoverAlign ?? "start"}
        {...(popoverSide ? { side: popoverSide } : {})}
        collisionPadding={12}
      >
        <div className="bg-white rounded-[24px] min-w-[310px]">
          {stepIndicator}

          {/* Whose follow-up (manager/admin only) — asked IN the modal. */}
          {step === "date" && slotPicker}

          {/* ───── STEP 1: DATE ───── */}
          {step === "date" && (
            <>
              <div className="flex items-center justify-between pl-4 pr-3 py-1 border-b border-[#e6e8e7]">
                <Select
                  value={format(currentMonth, "MMMM")}
                  onValueChange={(m) =>
                    setCurrentMonth(setMonth(currentMonth, MONTHS.indexOf(m)))
                  }
                >
                  <SelectTrigger className="h-auto px-2 py-2.5 gap-2 border-none shadow-none hover:bg-transparent rounded-full">
                    <span className="text-[14px] font-medium text-[#49454f] tracking-[-0.56px]">
                      {format(currentMonth, "MMMM yyyy")}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month) => (
                      <SelectItem key={month} value={month}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-10 rounded-full hover:bg-transparent"
                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  >
                    <ChevronLeft className="size-5 text-[#49454f]" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-10 rounded-full hover:bg-transparent"
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  >
                    <ChevronRight className="size-5 text-[#49454f]" />
                  </Button>
                </div>
              </div>

              <div className="px-3 py-0">
                <div className="grid grid-cols-7 gap-px">
                  {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
                    <div
                      key={`day-${index}`}
                      className="size-10 flex items-center justify-center"
                    >
                      <span className="text-[14px] font-normal text-black tracking-[-0.56px] leading-[1.35]">
                        {day}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-px">
                  {calendarDays.map((day, index) => {
                    const isCurrentMonth = isSameMonth(day, currentMonth);
                    const isToday = isSameDay(day, today);
                    const isSelected =
                      selectedDate && isSameDay(day, selectedDate);
                    const isSelectedButNotToday = isSelected && !isToday;
                    const isPast = isDisabledDay(day);
                    return (
                      <button
                        key={`date-${format(day, "yyyy-MM-dd")}-${index}`}
                        onClick={() => handleDateClick(day)}
                        disabled={isPast}
                        title={
                          isBeyondMax(day)
                            ? (maxDateHint ?? "Beyond the allowed follow-up window")
                            : undefined
                        }
                        className={`size-10 flex items-center justify-center rounded-full
                          ${!isCurrentMonth ? "text-[#9ba3a0]" : isPast ? "text-[#9ba3a0]" : "text-[#1e302a]"}
                          ${isSelectedButNotToday ? "bg-[#218760] text-white" : ""}
                          ${isToday ? " border border-[#218760] text-[#218760]" : ""}
                          ${isPast ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
                          ${!isSelected && !isToday && !isPast ? "hover:bg-gray-100" : ""}
                          transition-colors`}
                      >
                        <span className="text-[14px] font-normal tracking-[-0.56px] leading-[1.35]">
                          {format(day, "d")}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {maxDate != null && (
                  <p className="mt-1.5 mb-1 flex items-center gap-1.5 rounded-[10px] bg-[#f4f7f6] px-2.5 py-1.5 text-[11px] leading-snug text-[#697571]">
                    <CalendarDays className="size-3.5 shrink-0 text-[#218760]" />
                    <span>
                      {maxDateHint ??
                        `Follow-ups here can be set until ${format(maxDate, "dd MMM yyyy")}.`}
                    </span>
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between px-3 pt-1 pb-2 border-t border-[#e6e8e7]">
                {allowClear ? (
                  <Button
                    variant="ghost"
                    onClick={handleClear}
                    className="h-10 px-4 text-[14px] font-medium text-[#697571] tracking-[-0.28px] hover:bg-transparent rounded-[16px]"
                  >
                    Clear
                  </Button>
                ) : (
                  <span />
                )}
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => setOpen(false)}
                    className="h-10 px-4 text-[14px] font-medium text-[#697571] tracking-[-0.28px] hover:bg-transparent rounded-[16px]"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDateOK}
                    disabled={!selectedDate || slotPickerPending}
                    title={
                      slotPickerPending
                        ? slotPickerPendingReason ?? "Select whose follow-up first"
                        : undefined
                    }
                    className="h-10 px-4 text-[14px] font-medium text-[#218760] tracking-[-0.28px] hover:bg-transparent rounded-[16px] disabled:opacity-40"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* ───── STEP 2: TIME ───── */}
          {step === "time" && (
            <>
              <div className="px-4 pt-3 pb-1">
                <p className="text-[11px] font-medium text-[#697571] tracking-[-0.22px]">
                  {selectedDate ? format(selectedDate, "EEE, dd MMM yyyy") : ""}
                </p>
                <p className="text-[13px] font-medium text-[#1e302a] tracking-[-0.26px] mt-0.5">
                  Select time
                </p>
              </div>

              <div className="flex items-center justify-center gap-3 px-4 py-4">
                <div className="flex-1 max-w-[80px]">
                  <p className="text-[9px] font-medium text-[#9ba3a0] text-center mb-1 uppercase">
                    Hour
                  </p>
                  <ScrollSelector
                    items={HOURS_12}
                    selected={hour}
                    onSelect={setHour}
                  />
                </div>

                <span className="text-[24px] font-medium text-[#1e302a] mt-4">
                  :
                </span>

                <div className="flex-1 max-w-[80px]">
                  <p className="text-[9px] font-medium text-[#9ba3a0] text-center mb-1 uppercase">
                    Min
                  </p>
                  <ScrollSelector
                    items={MINUTES_5}
                    selected={minute}
                    onSelect={setMinute}
                    formatFn={(v) => String(v).padStart(2, "0")}
                  />
                </div>

                <div className="flex flex-col gap-1 mt-4">
                  {(["AM", "PM"] as const).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPeriod(p)}
                      className={`px-3 py-2 rounded-xl text-[12px] font-medium transition-all ${
                        period === p
                          ? "bg-[#218760] text-white"
                          : "bg-[#f7f9f8] text-[#697571] hover:bg-[#e6e8e7]"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div
                className={`mx-4 mb-3 p-3 rounded-xl border text-center ${timePast ? "bg-red-50/60 border-red-200" : "bg-[#f7f9f8] border-[#e6e8e7]"}`}
              >
                <span
                  className={`text-[20px] font-medium tracking-[-0.4px] ${timePast ? "text-[#EF5350]" : "text-[#1e302a]"}`}
                >
                  {hour}:{String(minute).padStart(2, "0")} {period}
                </span>
                {timePast && (
                  <p className="text-[10px] font-medium text-[#EF5350] mt-1">
                    This time has already passed today
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between px-3 pt-1 pb-2 border-t border-[#e6e8e7]">
                <Button
                  variant="ghost"
                  onClick={handleCancel}
                  className="h-10 px-4 text-[14px] font-medium text-[#697571] tracking-[-0.28px] hover:bg-transparent rounded-[16px]"
                >
                  Back
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleTimeOK}
                  disabled={timePast}
                  className="h-10 px-4 text-[14px] font-medium text-[#218760] tracking-[-0.28px] hover:bg-transparent rounded-[16px] disabled:opacity-40 disabled:text-[#9ba3a0]"
                >
                  Next
                </Button>
              </div>
            </>
          )}

          {/* ───── STEP 3: NOTE ───── */}
          {step === "note" && (
            <>
              <div className="px-4 pt-3 pb-1">
                <p className="text-[11px] font-medium text-[#697571] tracking-[-0.22px]">
                  {selectedDate ? format(selectedDate, "EEE, dd MMM yyyy") : ""}
                  {enableTime && (
                    <>
                      {" "}
                      · {hour}:{String(minute).padStart(2, "0")} {period}
                    </>
                  )}
                </p>
                <p className="text-[13px] font-medium text-[#1e302a] tracking-[-0.26px] mt-0.5">
                  {copy.noteTitle}
                </p>
              </div>

              <div className="px-4 py-3">
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={copy.notePlaceholder}
                  autoFocus
                  className="w-full min-h-[100px] p-3 rounded-xl border border-[#e6e8e7] bg-[#f7f9f8] text-[13px] text-[#1e302a] tracking-[-0.26px] resize-none focus:outline-none focus:ring-1 focus:ring-[#218760] focus:border-[#218760] placeholder:text-[#9ba3a0] transition-all"
                />
                <p className="text-[10px] font-normal text-[#9ba3a0] mt-1.5 tracking-[-0.2px]">
                  {copy.noteHint}
                </p>
              </div>

              <div className="flex items-center justify-between px-3 pt-1 pb-2 border-t border-[#e6e8e7]">
                <Button
                  variant="ghost"
                  onClick={handleCancel}
                  className="h-10 px-4 text-[14px] font-medium text-[#697571] tracking-[-0.28px] hover:bg-transparent rounded-[16px]"
                >
                  Back
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleCreate}
                  className="h-10 px-5 text-[14px] font-medium text-white bg-[#218760] hover:bg-[#1a6b4c] tracking-[-0.28px] rounded-[16px] transition-colors"
                >
                  {copy.submit}
                </Button>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
